import { isRecord } from './guards';

/** Error codes of the membership endpoints the storefront acts on beyond a plain classification. */
export const MEMBERSHIP_ERROR_CODE = {
  /** 503: the payment service did not answer, the payment outcome is unknown. */
  paymentUnavailable: 'MEMBERSHIP_PAYMENT_UNAVAILABLE',
  /** 502 with this code: the payment service refused the request, nothing was charged. */
  paymentRejected: 'MEMBERSHIP_PAYMENT_REJECTED',
  /** 404 of every client endpoint while the club is switched off on the backend. */
  disabled: 'MEMBERSHIPS_DISABLED',
} as const;

/**
 * How the payment service behind a request failed: `unknown` — it may have
 * charged, so `/me` decides before anything is offered again; `rejected` —
 * nothing was charged and a new attempt is safe.
 */
export type MembershipPaymentFault = 'unknown' | 'rejected';

/**
 * Classifies a failure of the payment service behind a membership request.
 * A 503 is `unknown` whatever its code, and so is `MEMBERSHIP_PAYMENT_UNAVAILABLE`
 * under any status; only `MEMBERSHIP_PAYMENT_REJECTED` is `rejected`. A bare
 * 502 without the code may come from a proxy (Cloudflare, nginx) after the
 * charge went through, so it is no promise that nothing was charged: it is
 * `null` here and, as any server fault, checked through `/me` by the callers.
 * `null` for every other failure.
 */
export const paymentFaultOf = (
  status: number | undefined,
  code: string | undefined,
): MembershipPaymentFault | null => {
  if (status === 503 || code === MEMBERSHIP_ERROR_CODE.paymentUnavailable) {
    return 'unknown';
  }
  if (code === MEMBERSHIP_ERROR_CODE.paymentRejected) {
    return 'rejected';
  }
  return null;
};

/**
 * What the backend says about the payment service call behind a 502 or a 503,
 * for the Sentry report. Never the upstream message or any id.
 */
export interface MembershipUpstream {
  /** Payment service call that failed, e.g. `charge`; `null` when not named. */
  operation: string | null;
  /** HTTP status the payment service answered with; `null` when it did not answer. */
  upstreamStatus: number | null;
}

/** The operation and upstream status of an error's `metadata`; `undefined` when it names neither. */
export const toUpstream = (
  metadata: unknown,
): MembershipUpstream | undefined => {
  if (!isRecord(metadata)) {
    return undefined;
  }
  if (!('operation' in metadata) && !('upstreamStatus' in metadata)) {
    return undefined;
  }
  return {
    operation:
      typeof metadata.operation === 'string' ? metadata.operation : null,
    upstreamStatus:
      typeof metadata.upstreamStatus === 'number'
        ? metadata.upstreamStatus
        : null,
  };
};

/** The club is switched off on the backend: a 404 with `MEMBERSHIPS_DISABLED`. */
export const isMembershipsDisabled = (
  status: number | undefined,
  code: string | undefined,
): boolean => status === 404 && code === MEMBERSHIP_ERROR_CODE.disabled;

/**
 * Thrown by the gateway when the club is switched off on the backend; the
 * storefront then treats the club as absent rather than as a failed load.
 */
export class MembershipsDisabledError extends Error {
  constructor() {
    super('Memberships are disabled');
    this.name = 'MembershipsDisabledError';
  }
}

/** `{ upstream }` for an incident when the backend named the payment service call, nothing otherwise. */
export const upstreamField = (
  upstream: MembershipUpstream | undefined,
): { upstream?: MembershipUpstream } => (upstream ? { upstream } : {});
